import {computed, DestroyRef, inject, Injectable, Signal, signal} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {Observable} from 'rxjs';
import type {Signal as SignalPayload} from '../../components/signals-screen/types';
import {ChatMessage, FIVE_MINUTES_IN_MS} from '../../components/copilot/copilot.model';
import {AlertPayload, WidgetContext, WizardPayload} from '../../components/copilot/types';
import {AuthorizationService} from '../authorization.service';
import {CopilotService} from '../copilot.service';
import {DateRangeValue} from '../../utils/time';
import {
  PolarisAccountKey,
  PolarisChatHistoryItemVm,
  PolarisChatId,
  PolarisChatVm,
  PolarisFacadeContract,
  PolarisFacadeViewState,
  PolarisMessageVm,
  PolarisOpenChatResultVm,
  PolarisPaginationParams,
  PolarisSendMessageOutcomeVm,
  PolarisSendMessageOptionsVm,
  PolarisStartChatOptionsVm,
  PolarisWidgetContextVm,
  PolarisWizardContext,
  PolarisWizardVm
} from './model';

interface LegacyChatVm {
  id: string;
  title: string;
  lastUpdate: number;
  messages: ChatMessage[];
  myBrand?: string;
  myCompetitors?: string[];
  wizard?: unknown;
  alert?: AlertPayload;
  signal?: SignalPayload;
  origin?: PolarisChatVm['origin'];
  widgetName?: string;
  widgetContext?: unknown;
  isCopilotThinking: Signal<boolean>;
}

@Injectable({
  providedIn: 'root'
})
export class CopilotLegacyAdapterService implements PolarisFacadeContract {
  private copilotService = inject(CopilotService);
  private authorizationService = inject(AuthorizationService);
  private destroyRef = inject(DestroyRef);

  private activeChatId = signal<PolarisChatId | null>(null);
  private accountKey = signal<PolarisAccountKey | null>(null);
  private recentChatId = signal<PolarisChatId | null>(null);
  private recentChatIdTimeout = signal<NodeJS.Timeout | null>(null);
  private customerNameSignal = signal<string>('');

  private isLoadingOverviewSignal = signal(false);
  private isLoadingActiveChatSignal = signal(false);
  private canLoadMoreChatsSignal = signal(false);

  private getLegacyChats(): Record<string, LegacyChatVm> {
    return this.copilotService.chats() as Record<string, LegacyChatVm>;
  }

  public readonly state: PolarisFacadeViewState = {
    chats: computed(() => {
      const legacyChats = this.getLegacyChats();
      const mapped: Record<PolarisChatId, PolarisChatVm> = {};

      Object.values(legacyChats).forEach(chat => {
        mapped[chat.id] = this.toChatVm(chat);
      });

      return mapped;
    }),
    chatHistoryItems: computed(() => {
      const legacyChats = this.getLegacyChats();
      return Object.values(legacyChats)
        .map(chat => this.toHistoryItemVm(chat))
        .sort((a, b) => b.lastUpdate - a.lastUpdate);
    }),
    activeChat: computed(() => {
      const chatId = this.activeChatId();
      if (!chatId) {
        return null;
      }
      return this.state.chats()[chatId] ?? null;
    }),
    activeChatMessages: computed(() => {
      const chatId = this.activeChatId();
      if (!chatId) {
        return [];
      }
      return this.state.chats()[chatId]?.messages ?? [];
    }),
    myBrand: this.copilotService.myBrand,
    myCompetitors: this.copilotService.myCompetitors,
    customerName: computed(() => this.customerNameSignal()),
    isLoadingOverview: computed(() => this.isLoadingOverviewSignal()),
    isLoadingActiveChat: computed(() => this.isLoadingActiveChatSignal()),
    isSendingMessage: computed(() => {
      const chats = this.getLegacyChats();
      return Object.values(chats).some(chat => chat.isCopilotThinking());
    }),
    canLoadMoreChats: computed(() => this.canLoadMoreChatsSignal()),
    recentChatId: computed(() => this.recentChatId()),
  };

  constructor() {
    this.authorizationService.userProfile$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(profile => {
        this.customerNameSignal.set(profile.firstName ?? '');
      });
  }

  public init(accountKey: PolarisAccountKey): void {
    this.accountKey.set(accountKey);
  }

  public getChat(chatId: PolarisChatId): PolarisChatVm | undefined {
    return this.state.chats()[chatId];
  }

  public getChatMessages(chatId: PolarisChatId): Signal<PolarisMessageVm[]> {
    return computed(() => this.state.chats()[chatId]?.messages ?? []);
  }

  public loadChatsOverview(_pagination: PolarisPaginationParams = {}): void {
    this.isLoadingOverviewSignal.set(false);
  }

  public async openChat(chatId: PolarisChatId): Promise<PolarisOpenChatResultVm> {
    this.isLoadingActiveChatSignal.set(true);
    try {
      const found = this.copilotService.ensureChatLoaded(chatId);
      if (found) {
        this.activeChatId.set(chatId);
      }
      return {
        found,
        loadedFromServer: false
      };
    } finally {
      this.isLoadingActiveChatSignal.set(false);
    }
  }

  public async ensureChatLoaded(chatId: PolarisChatId): Promise<boolean> {
    const result = await this.openChat(chatId);
    return result.found;
  }

  public async startChat(prompt: string, options?: PolarisStartChatOptionsVm): Promise<PolarisChatId> {
    const chat = this.copilotService.getNewChat(true);
    const { origin } = this.getResolvedLegacyContext(options);
    this.activeChatId.set(chat.id);
    this.copilotService.addUserMessage(
      chat.id,
      prompt,
      this.toWizardPayload(options?.wizard),
      this.toAlertPayload(options?.alert),
      options?.signal,
      origin,
      this.toWidgetContext(options?.widgetContext)
    );
    return chat.id;
  }

  public async sendMessage(
    chatId: PolarisChatId | null,
    prompt: string,
    options?: PolarisSendMessageOptionsVm
  ): Promise<PolarisSendMessageOutcomeVm> {
    if (!chatId) {
      return {status: 'failed'};
    }
    const chat = this.copilotService.getChatById(chatId);
    if (!chat) {
      return {status: 'chat_not_found'};
    }
    const { origin } = this.getResolvedLegacyContext(options);
    this.activeChatId.set(chatId);
    this.copilotService.addUserMessage(
      chatId,
      prompt,
      this.toWizardPayload(options?.wizard),
      this.toAlertPayload(options?.alert),
      options?.signal,
      origin,
      this.toWidgetContext(options?.widgetContext)
    );
    return {status: 'ok', chatId};
  }

  public async loadMoreChats(): Promise<void> {
    this.canLoadMoreChatsSignal.set(false);
  }

  public setUserBrandContext(brand: string | null, competitors: string[]): void {
    this.copilotService.myBrand.set(brand);
    this.copilotService.myCompetitors.set([...competitors]);
  }

  public getWizardsResponse(): Observable<PolarisWizardVm[]> {
    return this.copilotService.getWizardsResponse() as Observable<PolarisWizardVm[]>;
  }

  public setRecentChatId(chatId: PolarisChatId): void {
    this.recentChatId.set(chatId);
    this.clearRecentChatIdTimeout();
    this.recentChatIdTimeout.set(
      setTimeout(() => {
        this.resetRecentChatId();
      }, FIVE_MINUTES_IN_MS)
    );
  }

  public resetRecentChatId(): void {
    this.recentChatId.set(null);
    this.clearRecentChatIdTimeout();
  }

  private clearRecentChatIdTimeout(): void {
    const timeout = this.recentChatIdTimeout();
    if (timeout) {
      clearTimeout(timeout);
      this.recentChatIdTimeout.set(null);
    }
  }
  private toChatVm(chat: LegacyChatVm): PolarisChatVm {
    return {
      id: chat.id,
      title: chat.title,
      lastUpdate: chat.lastUpdate,
      messages: chat.messages.map(message => this.toMessageVm(message)),
      myBrand: chat.myBrand,
      myCompetitors: chat.myCompetitors,
      wizard: chat.wizard as PolarisWizardContext | undefined,
      alert: chat.alert,
      signal: chat.signal,
      origin: chat.origin,
      widgetName: chat.widgetName,
      widgetContext: chat.widgetContext as PolarisWidgetContextVm | undefined
    };
  }

  private toHistoryItemVm(chat: LegacyChatVm): PolarisChatHistoryItemVm {
    return {
      id: chat.id,
      title: chat.title,
      lastUpdate: chat.lastUpdate,
      myBrand: chat.myBrand,
      myCompetitors: chat.myCompetitors
    };
  }

  private toMessageVm(message: ChatMessage): PolarisMessageVm {
    return {
      id: message.id,
      author: message.author,
      content: message.content,
      graphs: message.graphs,
      timestamp: message.timestamp,
      followUpQuestions: message.followUpQuestions,
      error: message.error,
      insights: message.insights,
      creativesURLs: message.creativesURLs,
      interactionId: message.interactionId,
      userResponse: message.userResponse,
      origin: message.origin,
      widgetName: message.widgetName
    };
  }

  private toWizardPayload(wizard?: PolarisWizardContext): WizardPayload | undefined {
    if (!wizard) {
      return undefined;
    }
    const focusAreas: WizardPayload['focusAreas'] = {};
    Object.entries(wizard.focusAreas).forEach(([key, value]) => {
      focusAreas[key] = {
        title: key,
        subQuestions: value.subQuestions
      };
    });

    return {
      type: wizard.type,
      geos: wizard.geos,
      startDate: wizard.startDate,
      endDate: wizard.endDate,
      brands: [],
      competitors: [],
      focusAreas
    };
  }

  private toAlertPayload(alert?: AlertPayload): AlertPayload | undefined {
    return alert;
  }

  private toWidgetContext(widgetContext?: PolarisWidgetContextVm): WidgetContext | undefined {
    if (!widgetContext) {
      return undefined;
    }

    const dateRange = this.toLegacyDateRange(widgetContext.dateRange);
    if (!dateRange || !widgetContext.myBrand || !widgetContext.device) {
      return undefined;
    }

    return {
      name: widgetContext.name as WidgetContext['name'],
      metrics: widgetContext.metrics ?? [],
      myBrand: widgetContext.myBrand,
      myCompetitors: widgetContext.myCompetitors ?? [],
      dateRange,
      countries: widgetContext.countries ?? [],
      device: widgetContext.device as WidgetContext['device']
    };
  }

  private toLegacyDateRange(input: PolarisWidgetContextVm['dateRange']): DateRangeValue | undefined {
    if (!input || typeof input === 'string') {
      return undefined;
    }
    const startDate = new Date(input.from);
    const endDate = new Date(input.to);
    if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
      return undefined;
    }
    return {
      startDate,
      endDate
    };
  }

  private getResolvedLegacyContext(
    options?: PolarisSendMessageOptionsVm
  ): { origin?: PolarisChatVm['origin']; widgetName?: string } {
    const origin = options?.origin ?? options?.widgetContext?.origin;
    const widgetName = options?.widgetContext?.name;
    return { origin, widgetName };
  }
}
